/**
 * Helpers for GET /credits/capacity/ and capacity_exceeded upload errors.
 */

/** Unwrap DRF ErrorDetail lists: "x" | ["x"] → "x" */
const unwrap = (value) => {
    if (Array.isArray(value)) return value[0];
    return value;
};

/** Pull API body from axios error or already-unwrapped payload. */
export const getApiErrorBody = (errorOrBody) => {
    if (!errorOrBody) return null;
    if (errorOrBody.response) {
        const data = errorOrBody.response?.data;
        return data?.data ?? data ?? null;
    }
    if (errorOrBody.data && (errorOrBody.code || errorOrBody.detail)) {
        return errorOrBody.data;
    }
    return errorOrBody;
};

/**
 * Returns capacity_exceeded fields when present, else null.
 * Handles top-level payloads, field-nested ValidationError (e.g. tags),
 * and Renderer-stringified ErrorDetail blobs.
 */
export const parseCapacityExceeded = (errorOrBody) => {
    const body = getApiErrorBody(errorOrBody);
    if (!body) return null;

    const toCapacity = (obj) => {
        if (!obj || typeof obj !== "object") return null;
        const code = unwrap(obj.code);
        if (code !== "capacity_exceeded") return null;
        return {
            code: "capacity_exceeded",
            detail: String(unwrap(obj.detail) || "Capacity limit reached"),
            activity_code: unwrap(obj.activity_code) || null,
            used: Number(unwrap(obj.used) ?? 0),
            limit: Number(unwrap(obj.limit) ?? 0),
            extra: Number(unwrap(obj.extra) ?? 0),
            effective_limit: Number(
                unwrap(obj.effective_limit) ??
                    (Number(unwrap(obj.limit) || 0) + Number(unwrap(obj.extra) || 0))
            ),
        };
    };

    const fromErrorDetailString = (text) => {
        if (typeof text !== "string" || !text.includes("capacity_exceeded")) {
            return null;
        }
        const detailMatch =
            text.match(/string=['"]([^'"]+)['"]/) ||
            text.match(/(Maximum client tag limit reached\.[^'"]*)/);
        return {
            code: "capacity_exceeded",
            detail:
                detailMatch?.[1] ||
                "Maximum client tag limit reached. Buy credits to expand, then retry.",
            activity_code: "extra_tags_8_month",
            used: 0,
            limit: 0,
            extra: 0,
            effective_limit: 0,
        };
    };

    if (typeof body === "string") {
        return fromErrorDetailString(body);
    }

    if (typeof body !== "object") return null;

    const direct = toCapacity(body);
    if (direct) return direct;

    // Nested under field keys like { tags: { code, detail, ... } } or { tags: [...] }
    for (const value of Object.values(body)) {
        if (Array.isArray(value)) {
            for (const item of value) {
                const found = toCapacity(item) || fromErrorDetailString(String(item));
                if (found) return found;
            }
        } else if (value && typeof value === "object") {
            const found = toCapacity(value);
            if (found) return found;
        } else if (typeof value === "string") {
            const found = fromErrorDetailString(value);
            if (found) return found;
        }
    }

    return null;
};

/** Human label for a capacity catalog item. */
export const capacityItemTitle = (item) => {
    if (!item) return "Capacity";
    switch (item.key) {
        case "storage":
            return "Document storage";
        case "contacts":
            return "Contacts";
        case "custom_fields":
            return "Custom fields";
        case "client_tags":
            return "Client tags";
        default:
            return item.key;
    }
};

/** Format used/limit for display (storage in GB). */
export const formatCapacityUsage = (item) => {
    if (!item) return "";
    if (item.unit === "gb") {
        const used = Number(item.used || 0).toFixed(1);
        const limit = Number(item.effective_limit || 0).toFixed(0);
        return `${used} / ${limit} GB`;
    }
    return `${item.used ?? 0} / ${item.effective_limit ?? 0}`;
};

/** CTA copy for expanding one pack. */
export const expandCtaLabel = (item) => {
    if (!item) return "Expand with credits";
    const units = item.unit_per_pack ?? 0;
    const credits = item.credits_per_pack ?? 100;
    if (item.key === "storage") return `Add ${units} GB (${credits} credits)`;
    if (item.key === "contacts") return `Add ${units.toLocaleString()} contacts (${credits} credits)`;
    if (item.key === "custom_fields") return `Add ${units} fields (${credits} credits)`;
    if (item.key === "client_tags") return `Add ${units} tags (${credits} credits)`;
    return `Expand (${credits} credits)`;
};
